/**
 * Country-code TLDs (ISO 3166-1 alpha-2) plus the few exceptional ones in use.
 * Kept explicit so invented endings such as `.ll` are rejected.
 */
const COUNTRY_TLDS = new Set(
  `ac ad ae af ag ai al am ao aq ar as at au aw ax az ba bb bd be bf bg bh bi bj bl bm bn bo bq
   br bs bt bv bw by bz ca cc cd cf cg ch ci ck cl cm cn co cr cu cv cw cx cy cz de dj dk dm do
   dz ec ee eg eh er es et eu fi fj fk fm fo fr ga gb gd ge gf gg gh gi gl gm gn gp gq gr gs gt
   gu gw gy hk hm hn hr ht hu id ie il im in io iq ir is it je jm jo jp ke kg kh ki km kn kp kr
   kw ky kz la lb lc li lk lr ls lt lu lv ly ma mc md me mf mg mh mk ml mm mn mo mp mq mr ms mt
   mu mv mw mx my mz na nc ne nf ng ni nl no np nr nu nz om pa pe pf pg ph pk pl pm pn pr ps pt
   pw py qa re ro rs ru rw sa sb sc sd se sg sh si sj sk sl sm sn so sr ss st sv sx sy sz tc td
   tf tg th tj tk tl tm tn to tr tt tv tw tz ua ug uk um us uy uz va vc ve vg vi vn vu wf ws ye
   yt za zm zw`.split(/\s+/)
);

/** Generic TLDs we expect in practice. Add entries here when a new one is needed. */
const GENERIC_TLDS = new Set(
  `com net org info biz edu gov mil int arpa app dev page site online store shop blog cloud tech
   media news live life world today center church faith ministry mission bible group team work
   agency studio design digital email events forum help host institute network solutions systems
   services support tools video wiki xyz pro name mobi asia cat coop jobs post tel travel aero
   museum global one link click top club fun space website press art bio care family gives
   charity foundation ngo ong academy school education community social chat zone plus now
   cafe house`.split(/\s+/)
);

/**
 * Hostname structure: one or more DNS labels followed by an alphabetic TLD.
 * No trailing dot, so partial values such as `www.` or `www.link.` are rejected.
 */
const HOSTNAME_PATTERN = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** True when the string already has a URL scheme (`https:`, `javascript:`, …). */
function hasUrlScheme(value: string): boolean {
  return /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(value);
}

function hasKnownTld(hostname: string): boolean {
  const tld = hostname.slice(hostname.lastIndexOf('.') + 1);
  return COUNTRY_TLDS.has(tld) || GENERIC_TLDS.has(tld);
}

/**
 * Turns `biserica-lumina.ro` / `www.site.ro` into an absolute http(s) URL.
 * Returns `null` when the value is not a complete, usable web address.
 */
export function toAbsoluteHttpUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  if (/\s/.test(trimmed)) return null;

  const withProtocol = hasUrlScheme(trimmed) ? trimmed : `https://${trimmed}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;

    const hostname = parsed.hostname.toLowerCase();
    if (hostname.length > 253) return null;
    if (!HOSTNAME_PATTERN.test(hostname)) return null;
    if (!hasKnownTld(hostname)) return null;

    return parsed.href;
  } catch {
    return null;
  }
}

/** True when the value is a complete http(s) address. */
export function isValidWebUrl(url: string): boolean {
  return toAbsoluteHttpUrl(url) !== null;
}
